// import components
import { ControlledToggleButtonGroup } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import Button from '@sdb/ui/atoms/Button';
import { EntityDropDown } from './EntityDropdown';
import { PoolDropdown } from './PoolDropdown';

// import endpoints
import {
  ADMIN_SETTINGS_BULK_ADD_CLUSTER,
  ADMIN_SETTINGS_BULK_UPDATE_EMPLOYER,
  ADMIN_SETTINGS_BULK_UPDATE_POOLS
} from 'RestAPIs/endpoints';

// import hooks
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from 'react-query';
import { useCreateResource } from 'RestAPIs/useCreateResource';
import { useUpdateResource } from 'RestAPIs/useUpdateResource';

// import types
import { PlanUnit, PlanUnitLink } from '../types';
import { AxiosResponse } from 'axios';
import { HierarchyValue } from 'RestAPIs/hierarchy';
import { GridRowClassNameParams } from '@mui/x-data-grid-pro';
import { LinkMode, LinkPlanUnitFormProps } from './LinkPlanUnitForm.types';

// import utils
import { t } from 'i18next';
import { pluck, uniq } from 'ramda';
import {
  linkPlanUnitFormSchema,
  linkPlanUnitFormDefault
} from './utils/linkPlanUnitFormSchema';
import {
  HierarchyPlanUnit,
  getSelectedTreeHierarchy,
  isDescendant,
  isDirectDescendant
} from './utils/treeHierarchy';
import { getOrganizationalUnits } from './useLinkDirect';
import {
  StyledLinkPlanUnitFormBox,
  StyledTableBox,
  StyledTableCRUDLayout
} from './LinkPlanUnitForm.styles';
import { StyledBoxAutoMarginLeft } from 'components/shared/Box/Box.styles';
import { useGetColumns } from '../hooks/useGetColumns';

export const LinkPlanUnitForm: React.FC<LinkPlanUnitFormProps> = ({
  mode,
  onClose,
  details,
  selectedRow,
  hierarchy,
  hasReferenceDate
}) => {
  const [mergedRows, setMergedRows] = useState<
    PlanUnit[] | HierarchyPlanUnit[] | undefined
  >([]);
  const methods = useForm<{ linkMode: LinkMode }>({
    resolver: yupResolver(linkPlanUnitFormSchema(mode)),
    defaultValues: {
      linkMode: 'PLAN_UNIT_ONLY',
      ...linkPlanUnitFormDefault(mode, selectedRow)
    }
  });
  const queryClient = useQueryClient();
  const columns = useGetColumns({
    hasReferenceDate: !!hasReferenceDate,
    showFullTable: false
  });

  const { mutateAsync: savePool, isLoading: isPoolLoading } = useUpdateResource(
    {
      url: ADMIN_SETTINGS_BULK_UPDATE_POOLS,
      id: ''
    }
  );
  const { mutateAsync: saveEmployer, isLoading: isEmployerLoading } =
    useUpdateResource({
      url: ADMIN_SETTINGS_BULK_UPDATE_EMPLOYER,
      id: ''
    });
  const { mutateAsync: saveCluster, isLoading: isClusterLoading } =
    useCreateResource({
      url: ADMIN_SETTINGS_BULK_ADD_CLUSTER
    });

  const isLoading = isPoolLoading || isEmployerLoading || isClusterLoading;

  const onSubmit = async (data) => {
    const units =
      methods.watch('linkMode') === 'PLAN_UNIT_ONLY'
        ? []
        : await getOrganizationalUnits(
            selectedRow.brId,
            methods.watch('linkMode')
          );

    const ids = selectedRow.brId.toString().concat(',', units.join(','));
    let save: (() => Promise<AxiosResponse<any>>) | null = null;

    if (mode === PlanUnitLink.CLUSTER) {
      const convertedData = {
        brIds: ids,
        clusterId: data.entity?.value ? parseInt(data.entity.value) : null
      };
      save = saveCluster.bind(this, convertedData);
    }
    if (mode === PlanUnitLink.EMPLOYER) {
      const convertedData = {
        costCenterDynIds: uniq(pluck('brDynId', details)).join(','),
        employerId: data.entity?.value ? parseInt(data.entity.value) : null
      };
      save = saveEmployer.bind(this, convertedData);
    }
    if (mode === PlanUnitLink.REQUEST_POOL) {
      const convertedData = {
        costCenterIds: ids,
        poolId: data.requestPool?.value
          ? parseInt(data.requestPool.value)
          : null,
        empPoolId: selectedRow.empPool?.id ? selectedRow.empPool?.id : null
      };
      save = savePool.bind(this, convertedData);
    }
    if (mode === PlanUnitLink.EMPLOYEE_POOL) {
      const convertedData = {
        costCenterIds: ids,
        empPoolId: data.employeePool?.value
          ? parseInt(data.employeePool.value)
          : null,
        poolId: selectedRow.pool?.id ? selectedRow.pool?.id : null
      };
      save = savePool.bind(this, convertedData);
    }

    save?.().then(async () => {
      onClose();
      await queryClient.invalidateQueries(['hierarchy']);
    });
  };
  const getRowClassName = (
    params: GridRowClassNameParams<HierarchyPlanUnit>
  ) => {
    const { row } = params;
    const { linkMode } = methods.watch();

    if (!row || !selectedRow) return '';

    switch (linkMode) {
      case 'PLAN_UNIT_ONLY':
        return row.brId?.toString() === selectedRow.brId?.toString()
          ? 'highlighted-row'
          : '';
      case 'ONLY_DIRECT_CHILDREN':
        return isDirectDescendant(selectedRow as HierarchyValue, row)
          ? 'highlighted-row'
          : '';
      case 'PLAN_UNIT_AND_ALL_OF_ITS_CHILDREN':
        return isDescendant(selectedRow as HierarchyValue, row)
          ? 'highlighted-row'
          : '';
      default:
        return '';
    }
  };

  useEffect(() => {
    if (selectedRow && hierarchy) {
      setMergedRows(
        getSelectedTreeHierarchy(
          hierarchy as HierarchyPlanUnit[],
          selectedRow.brId.toString()
        )
      );
    }
  }, [selectedRow]);

  return (
    <MultiStepForm methods={methods} onSubmit={onSubmit}>
      <StyledLinkPlanUnitFormBox>
        {mode === PlanUnitLink.EMPLOYEE_POOL ||
        mode === PlanUnitLink.REQUEST_POOL ? (
          <PoolDropdown
            name={
              mode === PlanUnitLink.EMPLOYEE_POOL
                ? 'employeePool'
                : 'requestPool'
            }
          />
        ) : (
          <EntityDropDown mode={mode} />
        )}
      </StyledLinkPlanUnitFormBox>
      <ControlledToggleButtonGroup
        control={methods.control}
        name="linkMode"
        color="secondary"
        label={t('which')}
        aria-label="alignment"
        inputToggleButtons={[
          {
            label: t('linkModes.planUnitOnly'),
            value: 'PLAN_UNIT_ONLY' as LinkMode,
            selected: methods.watch('linkMode') === 'PLAN_UNIT_ONLY'
          },
          {
            label: t('linkModes.onlyDirectChildren'),
            value: 'ONLY_DIRECT_CHILDREN' as LinkMode,
            selected: methods.watch('linkMode') === 'ONLY_DIRECT_CHILDREN'
          },
          {
            label: t('linkModes.planUnitAndAllOfItsChildren'),
            value: 'PLAN_UNIT_AND_ALL_OF_ITS_CHILDREN' as LinkMode,
            selected:
              methods.watch('linkMode') === 'PLAN_UNIT_AND_ALL_OF_ITS_CHILDREN'
          }
        ]}
      />
      <StyledTableBox>
        <StyledTableCRUDLayout
          key={`${
            hasReferenceDate ? 'tree-linkPlanUnitForm' : 'flat-linkPlanUnitForm'
          }`}
          getRowClassName={getRowClassName}
          editable={false}
          hasTreeData={hasReferenceDate}
          getRowId={(data) => (data?.__rowId ? data?.__rowId : data?.brId)}
          {...(hasReferenceDate && {
            getTreeDataPath: (row) => row.hierarchy ?? []
          })}
          rows={(mergedRows as HierarchyPlanUnit[]) ?? []}
          hideCreateAction
          columns={columns}
          collapse={-1}
          actionsThreshold={2}
          initialState={{
            sorting: {
              sortModel: [{ field: 'code', sort: 'asc' }]
            }
          }}
        />
      </StyledTableBox>
      <MultiStepForm.Actions>
        <StyledBoxAutoMarginLeft>
          <Button
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="primary"
          >
            {t('save')}
          </Button>
        </StyledBoxAutoMarginLeft>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
